import { useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Scrollbar } from 'swiper';
import EcoImg from 'eco-design/base/eco-img';
import { saveAICookieForShopify } from '@elem/ui-common/utils/CookieForShopify';
import { cn, useScrollAnimation } from 'elem/compPage/battery-backup-for-home/scroll-animation-v2';
import css from './index.module.scss';
import { ItfSceneProductCard, ItfSceneProductsProps } from './type';
import { useSceneProducts } from './useSceneProducts';
import { useGlobal } from '@/utils/useGlobal';

const DEFAULT_TEXTS = {
    buy: 'Add to Cart',
    buyNow: 'Buy Now',
    more: 'Learn More',
    out: 'Sold Out',
    listName: 'Products',
    worksWith: 'Works Best With',
};

const ProductCard = ({
    card,
    index,
    locale,
    visible,
    copy,
    onBuy,
    onMore,
}: {
    card: ItfSceneProductCard;
    index: number;
    locale: string;
    visible: boolean;
    copy: typeof DEFAULT_TEXTS;
    onBuy: (card: ItfSceneProductCard) => void;
    onMore: (card: ItfSceneProductCard, url: string) => void;
}) => {
    const features = useMemo(() => {
        const desc = card.descArr || card.desc;
        if (Array.isArray(desc)) return desc.filter((item) => item?.trim());
        return desc
            ? String(desc)
                  .split('\n')
                  .filter((item) => item.trim())
            : [];
    }, [card.desc, card.descArr]);

    const name = card.skuTitle || card.title || card.productTitle || '';
    const hasInventoryInfo = card.inventoryQuantity !== undefined && card.inventoryQuantity !== null;
    const soldOut = hasInventoryInfo && Number(card.inventoryQuantity) <= 0 && card.inventoryPolicy !== 'continue';
    const canBuy = Boolean(card.variantId);
    const buyUrl = card.link_buy || `https://${locale}.ecoflow.com/cart/${card.variantId}:1?channel=buy_button`;
    const moreUrl = card.link_more || (card.handle && card.variantId ? `https://${locale}.ecoflow.com/products/${card.handle}?variant=${card.variantId}` : '');

    return (
        <div className={cn(visible, 'product-card fade-up')} style={{ '--animate-delay': `${index * 0.15}s` } as React.CSSProperties}>
            <div className="product-image">
                <EcoImg pc={card.variantImg || card.img} alt={name} />
            </div>
            <div className="product-content">
                <div className="product-info">
                    {!!card.label && <div className="product-label">{card.label}</div>}
                    <div className="product-title">{name}</div>
                    {!!features.length && (
                        <ul className="product-features">
                            {features.map((feature, featureIndex) => (
                                <li key={featureIndex}>{feature}</li>
                            ))}
                        </ul>
                    )}
                    {!!card.worksWith && (
                        <div className="product-works-with">
                            <div className="works-with-label">{copy.worksWith}</div>
                            <div className="works-with-text">{card.worksWith}</div>
                        </div>
                    )}
                </div>
                <div className="product-actions">
                    {canBuy &&
                        (soldOut ? (
                            <button className="btn-cart" disabled>
                                {copy.out}
                            </button>
                        ) : (
                            <a href={buyUrl} className="btn-cart" target="_blank" rel="noreferrer" onClick={() => onBuy(card)}>
                                {copy.buyNow}
                            </a>
                        ))}
                    {!!moreUrl && (
                        <a href={moreUrl} className="btn-learn" target="_blank" rel="noreferrer" onClick={() => onMore(card, moreUrl)}>
                            {copy.more}
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

export const SceneProducts: React.FC<ItfSceneProductsProps> = ({ cards, id = 'scene-products', texts, className, maxWidth = 1200 }) => {
    const { useRouter, gtm } = useGlobal();
    const { locale = 'us' } = useRouter();
    const cardLists = useSceneProducts(cards, locale);
    const copy = { ...DEFAULT_TEXTS, ...texts };
    const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

    const handleBuy = (card: ItfSceneProductCard) => {
        try {
            gtm.push({
                event: 'uaEvent',
                event_name: 'buy',
                list_name: copy.listName,
                product_name: card.title || card.productTitle || card.skuTitle,
                price: card.price,
                button_name: copy.buyNow,
            });
            // @ts-ignore
            window?.fbq && window.fbq('track', 'Search');
        } catch (error) {
            console.error(error);
        }
        const buyUrl = card.link_buy || `https://${locale}.ecoflow.com/cart/${card.variantId}:1?channel=buy_button`;
        saveAICookieForShopify(buyUrl, locale);
    };

    const handleMore = (card: ItfSceneProductCard, url: string) => {
        saveAICookieForShopify(url, locale);
        gtm.push({
            event: 'uaEvent',
            event_name: 'select_product',
            list_name: copy.listName,
            product_name: card.skuTitle || card.title || card.productTitle,
            button_name: copy.more,
        });
    };

    if (!cardLists.length) return null;

    return (
        <div
            ref={ref}
            id={id}
            className={`${css['scene-products']}${className ? ` ${className}` : ''}`}
            style={{ '--scene-product-max-width': `${maxWidth}px` } as React.CSSProperties}
        >
            <div className="products-grid" style={{ '--scene-product-columns': Math.min(cardLists.length, 4) } as React.CSSProperties}>
                {cardLists.map((card, index) => (
                    <ProductCard
                        key={card.variantId || index}
                        card={card}
                        index={index}
                        locale={locale}
                        visible={isVisible}
                        copy={copy}
                        onBuy={handleBuy}
                        onMore={handleMore}
                    />
                ))}
            </div>
            <div className="products-swiper">
                <Swiper modules={[Scrollbar]} slidesPerView="auto" spaceBetween={16} scrollbar={{ hide: false, draggable: true }}>
                    {cardLists.map((card, index) => (
                        <SwiperSlide key={card.variantId || index}>
                            <ProductCard card={card} index={index} locale={locale} visible={isVisible} copy={copy} onBuy={handleBuy} onMore={handleMore} />
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </div>
    );
};

export default SceneProducts;
